import {
	CaseReducer,
	createAsyncThunk,
	createSlice,
	PayloadAction } from "@reduxjs/toolkit"
import { SignInContext, SignInRequest, SignInResponse } from "../globalContext/login.interface"
import { IThunkApiExtra, RootState } from "../store"
import { STATUS } from "../globalContext/globalContext.interfaces"

type LogInState = SignInContext & {
	isGuest: boolean;
};

export const initialState: LogInState = {
	logInAttemptsLeft: 5,
	logInStatus: undefined,
	cookie: '',
	uri: '',
	tokensData: {
		token: '',
		refreshToken: ''
	},
	status: STATUS.init,
	message: '',
	statusCode: 0,
	isGuest: false,
};

export const signIn = createAsyncThunk<SignInResponse, SignInRequest, { extra: IThunkApiExtra }>(
	'login/signin',
	async (request: SignInRequest | undefined, thunkApi) => {
		const { rejectWithValue, extra } = thunkApi;
		if (request === undefined) return rejectWithValue("Se necesitan datos del usuario");

		const { email, password } = request ?? {};
		if (email === undefined || email === null || email === '' || password === undefined || password === null || password === '') return rejectWithValue("El email y/o contraseña no debe venir vacío");

		const { services } = extra;
		const servicesList = (await services()).setupServices()
		const response = await servicesList.signIn(request)
		return response;
	}
)

const setLogInAttemptsLeftReducer: CaseReducer<LogInState, PayloadAction<number>> = (state, action) => {
	state.logInAttemptsLeft = action.payload;
};

const setURI: CaseReducer<LogInState, PayloadAction<string>> = (state, action) => {
	state.uri = action.payload;
};

const setResetValuesReducer: CaseReducer<LogInState> = (state) => {
	state.logInAttemptsLeft = 6;
	state.logInStatus = undefined;
	state.statusCode = 0;
	state.isGuest = false;
};

const setIsGuestReducer: CaseReducer<LogInState, PayloadAction<boolean>> = (state, action) => {
	state.isGuest = action.payload;
};

export const LoginSlice = createSlice({
	name: 'login',
	initialState,
	reducers: {
		setLogInAttemptsLeft: setLogInAttemptsLeftReducer,
		setResetValues: setResetValuesReducer,
		setValueURI: setURI,
		setIsGuest: setIsGuestReducer,
	},
	extraReducers: builder => {
		builder.addCase(signIn.pending, (state, action) => {
			state.status = STATUS.pending;
		});
		builder.addCase(signIn.fulfilled, (state, action) => {
			state.status = action.payload.message !== undefined ? STATUS.error : STATUS.idle;
			state.logInAttemptsLeft = action.payload.logInAttemptsLeft;
			state.tokensData = action.payload.body?.tokensData ?? {};
			state.logInStatus = action.payload.statusCode === 200;
			state.uri = action.payload.body?.uri;
			state.statusCode = action.payload.statusCode;
		});
		builder.addCase(signIn.rejected, (state, action) => {
			state.status = STATUS.error;
			state.message = action.error ? action.error.message : "Ha ocurrido un error";
		});
	},
});

export const selectIsGuest = (state: RootState) => state.LoginState.isGuest;
export const selectLogInAttemptsLeft = (state: RootState) => state.LoginState.logInAttemptsLeft;
export const selectLogInStatus = (state: RootState) => state.LoginState.logInStatus;
export const selectStatusCode = (state: RootState) => state.LoginState.statusCode;
export const selectTokensData = (state: RootState) => state.LoginState.tokensData;
export const selectStatus = (state: RootState) => state.LoginState.status;
export const selectURI = (state: RootState) => state.LoginState.uri;

export const { 
	setLogInAttemptsLeft, 
	setResetValues, 
	setValueURI, 
	setIsGuest
} = LoginSlice.actions;
